import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import CustomModal from '@/components/common/elements/Modal';
import { FilterPanel, FilterPanelControls, useIsMobileWidth } from '@/components/common/filter';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';

/**
 * 명치사 커리어 등록 안내(시안 A안, 2026-09-29 사용자 선택).
 * 정산계좌·강사 약관 동의가 없는 강사에게 무엇이 비었는지 보여 주고 강사 정보 화면으로 보낸다.
 * 폰은 아래 시트(공용 FilterPanel — 끌기·뒤로가기·바깥·Esc 로 닫힘), PC 는 작은 창(바깥·Esc 로 닫힘).
 * 어떻게 닫든 유형은 일반 그대로다. 열려 있을 때만 그린다.
 */

/** "오늘은 그만 보기" — 회원별 키에 그날 한국 날짜를 적어 둔다 */
const HIDE_KEY_PREFIX: string = 'mc_career_gate_hidden_';
const KST_OFFSET_MS: number = 9 * 60 * 60 * 1000;

/** 지금 한국 날짜(YYYY-MM-DD) */
const kstToday = (): string => new Date(Date.now() + KST_OFFSET_MS).toISOString().slice(0, 10);

/** 저장소가 막힌 환경(사생활 보호 모드 등)에서는 이번 방문 동안만 기억한다 */
const memoryHidden: Record<string, string> = {};

export const isCareerGateHiddenToday = (memberKey: string): boolean => {
  const key: string = HIDE_KEY_PREFIX + memberKey;
  if (memoryHidden[key] === kstToday()) return true;
  try {
    return localStorage.getItem(key) === kstToday();
  } catch {
    return false;
  }
};

export const hideCareerGateToday = (memberKey: string): void => {
  const key: string = HIDE_KEY_PREFIX + memberKey;
  memoryHidden[key] = kstToday();
  try {
    localStorage.setItem(key, memoryHidden[key]);
  } catch {
    // 저장이 막힌 환경 — 메모리 값으로 이번 방문 동안만 막는다
  }
};

// ─── 모양 ─────────────────────────────────────────────────────────────

const DANGER_SOFT: string = `${FORM_COLOR.danger}14`;

const Heading = styled.span`
  display: block;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.35;
  color: ${FORM_COLOR.text};
  word-break: keep-all;
`;

const Sub = styled.p`
  margin: 6px 0 10px;
  font-size: 14px;
  line-height: 1.5;
  color: ${FORM_COLOR.sub};
  word-break: keep-all;
  /* 좁은 폭(375)에서 "드려요." 한 단어만 떨어지지 않게 두 줄을 고르게 */
  text-wrap: balance;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-top: 1px solid ${FORM_COLOR.lineSoft};
`;

const StatusIcon = styled.span<{ ok: boolean }>`
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ ok }) => (ok ? FORM_COLOR.primarySoft : DANGER_SOFT)};
  color: ${({ ok }) => (ok ? FORM_COLOR.primaryDeep : FORM_COLOR.danger)};

  svg {
    font-size: 17px;
  }
`;

const ItemText = styled.div`
  flex: 1;
  min-width: 0;

  b {
    display: block;
    font-size: 15px;
    font-weight: 700;
    color: ${FORM_COLOR.text};
  }

  span {
    font-size: 13px;
    color: ${FORM_COLOR.muted};
  }
`;

const Tag = styled.span<{ ok: boolean }>`
  flex: none;
  padding: 4px 9px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ ok }) => (ok ? FORM_COLOR.primarySoft : DANGER_SOFT)};
  color: ${({ ok }) => (ok ? FORM_COLOR.primaryDeep : FORM_COLOR.danger)};
`;

const Benefit = styled.div`
  margin-top: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${FORM_COLOR.primaryCard};

  div {
    font-size: 13.5px;
    line-height: 1.7;
    color: ${FORM_COLOR.primaryDeep};
    word-break: keep-all;
  }

  b {
    font-weight: 800;
  }
`;

const Actions = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MainButton = styled.button`
  height: 52px;
  border: none;
  border-radius: 14px;
  background: ${FORM_COLOR.primary};
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
`;

const SubButton = styled.button`
  height: 52px;
  border: 1px solid ${FORM_COLOR.border};
  border-radius: 14px;
  background: #fff;
  color: ${FORM_COLOR.sub};
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
`;

const HideToday = styled.label`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 0 8px;
  font-size: 13.5px;
  color: ${FORM_COLOR.muted};
  cursor: pointer;
  user-select: none;

  input {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
    width: 18px;
    height: 18px;
    border: 1.5px solid ${FORM_COLOR.placeholder};
    border-radius: 5px;
    background: #fff;
    display: grid;
    place-content: center;
    cursor: pointer;
  }

  input:checked {
    border-color: ${FORM_COLOR.primary};
    background: ${FORM_COLOR.primary};
  }

  input:checked::after {
    content: '';
    width: 5px;
    height: 9px;
    margin-top: -2px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  input:focus-visible {
    outline: 2px solid ${FORM_COLOR.primary};
    outline-offset: 2px;
  }
`;

const PcBody = styled.div`
  display: flex;
  flex-direction: column;
`;

// ─── 부품 ─────────────────────────────────────────────────────────────

export interface CareerGateSheetProps {
  open: boolean;
  hasAccount: boolean;
  hasTerms: boolean;
  /** 수정 화면 — 이미 등록된 세미나라 보조 버튼을 "일반 세미나로 둘게요"로 */
  editMode?: boolean;
  /** [정보 입력하러 가기] — 창이 닫힌 뒤 불린다. hideToday = "오늘은 그만 보기" 체크 여부 */
  onGoEdit: (hideToday: boolean) => void;
  /** 그 밖의 모든 닫기(일반 유지) */
  onClose: (hideToday: boolean) => void;
}

const TITLE: React.ReactNode = (
  <Heading>
    명치사 커리어로 열려면
    <br />
    두 가지만 채워 주세요
  </Heading>
);

const Body = ({ hasAccount, hasTerms }: { hasAccount: boolean; hasTerms: boolean }) => (
  <>
    <Sub>참가비를 명치사가 받아 매월 10일 강사님 계좌로 정산해 드려요.</Sub>
    <Item>
      <StatusIcon ok={hasAccount} aria-hidden>{hasAccount ? <CheckRoundedIcon /> : <PriorityHighRoundedIcon />}</StatusIcon>
      <ItemText>
        <b>정산계좌</b>
        <span>참가비를 받을 계좌</span>
      </ItemText>
      <Tag ok={hasAccount}>{hasAccount ? '완료' : '미등록'}</Tag>
    </Item>
    <Item>
      <StatusIcon ok={hasTerms} aria-hidden>{hasTerms ? <CheckRoundedIcon /> : <PriorityHighRoundedIcon />}</StatusIcon>
      <ItemText>
        <b>강사 약관 동의</b>
        <span>정산·환불·운영 원칙</span>
      </ItemText>
      <Tag ok={hasTerms}>{hasTerms ? '완료' : '미동의'}</Tag>
    </Item>
    {/* 출석은 강사가 참여자 관리에서 체크한다(명치사가 대신 받지 않음) — 출석(참석완료)한 참가자만 이력서에 남는다 */}
    <Benefit>
      <div><b>커리어로 열면</b> · 신청·결제를 명치사가 대신 받고</div>
      <div>· 출석 체크한 참가자는 이력서에 수료가 남아요</div>
    </Benefit>
  </>
);

const CareerGatePanel = ({ hasAccount, hasTerms, editMode = false, onGoEdit, onClose }: Omit<CareerGateSheetProps, 'open'>) => {
  const mobile: boolean = useIsMobileWidth();
  const [hideToday, setHideToday] = useState<boolean>(false);
  const hideRef = useRef<boolean>(false);
  hideRef.current = hideToday;

  // PC 작은 창(MUI Modal)도 열린 동안 당겨서 새로고침·스와이프 뒤로가기가 쉬게 표시한다(잠금 자체는 MUI 가 한다)
  useEffect(() => {
    if (mobile) return undefined;
    const body: HTMLElement = document.body;
    if (body.dataset.scrollLock === 'true') return undefined;
    body.dataset.scrollLock = 'true';
    return () => {
      delete body.dataset.scrollLock;
    };
  }, [mobile]);

  const hideCheck = (
    <HideToday>
      <input type="checkbox" checked={hideToday} onChange={(e) => setHideToday(e.target.checked)} />
      오늘은 그만 보기
    </HideToday>
  );

  const actions = (goEdit: () => void, close: () => void) => (
    <Actions>
      <MainButton type="button" onClick={goEdit}>정보 입력하러 가기</MainButton>
      <SubButton type="button" onClick={close}>{editMode ? '일반 세미나로 둘게요' : '일반 세미나로 등록할게요'}</SubButton>
      {hideCheck}
    </Actions>
  );

  if (mobile) {
    return (
      <FilterPanel
        open
        anchorEl={null}
        title={TITLE}
        onClose={() => onClose(hideRef.current)}
        onApply={() => onGoEdit(hideRef.current)}
        sheetMaxHeight="92dvh"
        renderFooter={(controls: FilterPanelControls) => actions(controls.apply, controls.close)}
      >
        <Body hasAccount={hasAccount} hasTerms={hasTerms} />
      </FilterPanel>
    );
  }

  return (
    <CustomModal
      open
      onClose={() => onClose(hideRef.current)}
      width="min(420px, calc(100vw - 2rem))"
      height="auto"
      padding="28px 24px 16px"
    >
      <PcBody role="dialog" aria-label="명치사 커리어 등록 안내">
        {TITLE}
        <Body hasAccount={hasAccount} hasTerms={hasTerms} />
        <div style={{ display: 'flex', marginTop: 16 }}>
          {actions(() => onGoEdit(hideRef.current), () => onClose(hideRef.current))}
        </div>
      </PcBody>
    </CustomModal>
  );
};

/** 명치사 커리어 등록 안내 — 열려 있을 때만 그린다 */
const CareerGateSheet = ({ open, ...rest }: CareerGateSheetProps) => (open ? <CareerGatePanel {...rest} /> : null);

export default CareerGateSheet;
